import { from, Observable, of, timer } from 'rxjs';
import { ofType } from 'redux-observable';
import { find } from 'lodash';
import { catchError, map, mapTo, mergeMap, switchMap, takeUntil, withLatestFrom, delay } from 'rxjs/operators';
import JobsService from '../services/jobs.service';
import {
  LoadJobs,
  JobsActions,
  JobAction,
  LoadJobsSuccess,
  LoadJobsError,
  LoadJob,
  LoadJobSuccess,
  LoadJobError,
  StartJob,
  StartJobSuccess,
  StartJobError,
  RemoveJobSuccess,
  RemoveJobError,
  RemoveJob,
  CreateJob,
  CreateJobSuccess,
  CreateJobError,
  ReassignJob,
  ReassignJobSuccess,
  ReassignJobError,
  UpdateJob,
  UpdateJobSuccess,
  UpdateJobError,
  CompleteJob,
  CompleteJobSuccess,
  CompleteJobError,
  UpdateJobPhase,
  UpdateJobPhaseSuccess,
  UpdateJobPhaseError,
  StartJobsPoll, CancelJob, CancelJobSuccess, CancelJobError, SearchJobs, SearchJobsSuccess, SearchJobsError,
  StartSearchJobsPolling,
  StartJobsPollingAfterJobCreation,
  LoadSessionInfoForTasks,
  LoadSessionInfoForTasksSuccess,
  LoadSessionInfoForTasksError,
  LoadFeedbacks,
  LoadFeedbacksSuccess,
  LoadFeedbacksError,
} from '../actions';
import { Job, JobPhase, Operation } from '../../shared/models/jobs';
import { CreateErrorNotice } from '../../store/actions/toaster.action';
import { RootState } from '../../store/state';

const JOBS_POLL_TIME = 30*1000;
const JOBS_REFRESH_TIMEOUT_ON_CREATE = 5*1000;

const createJobEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<CreateJob>(JobsActions.CreateJob),
  switchMap((action) => from(JobsService.createJob(action.payload.orgId, action.payload.body)).pipe(
    mergeMap((data: Job) => of(
      new CreateJobSuccess(data),
      new StartJob({
        id: data._id,
        includeChildren: action.payload.includeChildren
      }),
    )),
    catchError((error: Error) => of(
      new CreateJobError(error.message || 'Unknown job creation error.'),
      new CreateErrorNotice('Error', 'Failed to create job.'),
    )),
  )),
);

const updateJobEpic = (action$: Observable<JobAction>, $state: Observable<RootState>) => action$.pipe(
  ofType<UpdateJob>(JobsActions.UpdateJob),
  withLatestFrom($state),
  mergeMap(([action, state]) => {
    const job = find(state.jobs?.search?.data, {_id: action.jobID});
    return from((job?.start_time ? JobsService.updateStartedJob : JobsService.updateJob)(action.jobID, action.body)).pipe(
      mergeMap(() => of(
        new UpdateJobSuccess(),
        new LoadJob(action.jobID),
      )),
      catchError((error: Error) => of(
        new UpdateJobError(error.message || 'Unknown job update error.'),
        new CreateErrorNotice('Error', 'Failed to update job.'),
      )),
    )
  }),
);

export const jobsPollEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<StartJobsPoll>(JobsActions.StartJobsPoll),
  switchMap((action) => {
    return timer(0, JOBS_POLL_TIME).pipe(
      mapTo(new LoadJobs(action.orgID, action.params)),
      takeUntil(action$.pipe(
        ofType(JobsActions.StopJobsPoll)
      ))
    );
  })
);

const getJobsEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<LoadJobs>(JobsActions.LoadJobs),
  switchMap((action) => from(JobsService.getJobs(action.orgID, action.params)).pipe(
    map((data: Job[]) => new LoadJobsSuccess(data)),
    catchError((error: Error) => of(
      new LoadJobsError(error.message || 'Unknown job fetching error.'),
      new CreateErrorNotice('Error', 'Failed to load jobs.'),
    )),
  ))
);

const searchJobsEpic = (action$: Observable<JobAction>, $state: Observable<RootState>) => action$.pipe(
  ofType<SearchJobs>(JobsActions.SearchJobs),
  withLatestFrom($state),
  switchMap(([action, state]) => from(JobsService.searchJobs(action.orgID, action.params)).pipe(
    mergeMap((data: any) => {
      const jobs = data?.results ?? [];
      const jobIds = jobs.map(t => t?._id);
      const feedbackJobIds = jobs.filter(
        job => [JobPhase.Feedback, JobPhase.Completed].includes(job?.phase)
      ).map(job => job?._id);

      const actions: JobAction[] = [
        new SearchJobsSuccess({ ...data, params: action.params }),
      ];

      if (feedbackJobIds?.length > 0) {
        actions.push(new LoadFeedbacks(feedbackJobIds));
      }

      const isSessionsEnabled = state.orgHierarchy?.selectedOrg?.config?.sessions?.enabled;
      if (isSessionsEnabled) {
        actions.push(new LoadSessionInfoForTasks(jobIds));
      }

      return of(...actions);
    }),
    catchError((error: Error) => of(
      new SearchJobsError(error.message || 'Unknown job searching error.'),
      new CreateErrorNotice('Error', 'Failed to search jobs.'),
    )),
  ))
);

export const searchJobsPollEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<StartSearchJobsPolling>(JobsActions.StartSearchJobsPolling),
  switchMap((action) => {
    return timer(0, JOBS_POLL_TIME).pipe(
      mapTo(new SearchJobs(action.orgID, action.params)),
      // A new StartSearchJobsPolling is handled by switchMap itself (it unsubscribes
      // this timer and subscribes a fresh one) - listing it here too would let a
      // same-tick Stop dispatched for an older polling session race with and cancel
      // the timer a newer Start just created.
      takeUntil(action$.pipe(
        ofType(JobsActions.StopSearchJobsPolling),
      ))
    );
  })
);

const getJobEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<LoadJob>(JobsActions.LoadJob),
  mergeMap((action) => from(JobsService.getJob(action.id)).pipe(
    map((data: Job) => new LoadJobSuccess(data)),
    catchError((error: Error) => of(
      new LoadJobError(error.message || 'Unknown job fetching error.'),
      new CreateErrorNotice('Error', 'Failed to load job.'),
    )),
  ))
);

const startJobEpic = (action$: Observable<JobAction>, $state: Observable<RootState>) => action$.pipe(
  ofType<StartJob>(JobsActions.StartJob),
  withLatestFrom($state),
  mergeMap(([action, state]) => from(JobsService.start(action.payload.id)).pipe(
    mergeMap((data: Operation & {org_id: number}) => of(
      new StartJobSuccess(data),
      new StartJobsPollingAfterJobCreation(state.orgHierarchy?.rootOrg?._id,{
        skip_records: 0,
        size: 10,
        org_ids: [state.orgHierarchy?.selectedOrg?._id || data?.org_id],
        ...state.jobs.search.params
      })
    )),
    catchError((error: Error) => of(
      new StartJobError(error.message || 'Unknown job start error.'),
      new CreateErrorNotice('Error', 'Failed to start job.'),
    )),
  )),
);

const startJobsPollingAfterJobCreationEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<StartJobsPollingAfterJobCreation>(JobsActions.StartJobsPollingAfterJobCreation),
  delay(JOBS_REFRESH_TIMEOUT_ON_CREATE),
  mergeMap((action) => of(
    new SearchJobs(action.orgID, action.params)
  )),
);

const completeJobEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<CompleteJob>(JobsActions.CompleteJob),
  mergeMap((action) => from(JobsService.complete(action.id)).pipe(
    mergeMap(() => of(
      new CompleteJobSuccess(action.id),
      new LoadJob(action.id),
    )),
    catchError((error: Error) => of(
      new CompleteJobError(error.message || 'Unknown job complete error.'),
      new CreateErrorNotice('Error', 'Failed to complete job.'),
    )),
  )),
);

const updateJobPhaseEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<UpdateJobPhase>(JobsActions.UpdateJobPhase),
  mergeMap((action) => from(JobsService.updateJobPhase(action.id, action.phase)).pipe(
    mergeMap(() => of(
      new UpdateJobPhaseSuccess(),
      new LoadJob(action.id),
    )),
    catchError((error: Error) => of(
      new UpdateJobPhaseError(error.message || 'Unknown job phase update error.'),
      new CreateErrorNotice('Error', 'Failed to update job phase.'),
    )),
  )),
);

const removeJobEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<RemoveJob>(JobsActions.RemoveJob),
  mergeMap((action) => from(JobsService.remove(action.id)).pipe(
    map((data: number) => new RemoveJobSuccess(data)),
    catchError((error: Error) => of(
      new RemoveJobError(error.message || 'Unknown job remove error.'),
      new CreateErrorNotice('Error', 'Failed to remove job.'),
    )),
  )),
);

const cancelJobEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<CancelJob>(JobsActions.CancelJob),
  mergeMap(({ id }) => from(JobsService.cancel(id)).pipe(
    mergeMap((jobID: number) => of(
      new CancelJobSuccess(jobID),
      new CompleteJob(jobID),
    )),
    catchError((error: Error) => of(
      new CancelJobError(error.message || 'Unknown job cancel error.'),
      new CreateErrorNotice('Error', 'Failed to cancel job.'),
    )),
  )),
);

const reassignJobEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<ReassignJob>(JobsActions.ReassignJob),
  mergeMap((action) => from(JobsService.reassign(action.jobID, action.body)).pipe(
    map((data: Job) => new ReassignJobSuccess(data)),
    catchError((error: Error) => of(
      new ReassignJobError(error.message || 'Unknown reassign job error.'),
      new CreateErrorNotice('Error', 'Failed to reassign job.'),
    )),
  )),
);

const loadSessionForTasksEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<LoadSessionInfoForTasks>(JobsActions.LoadSessionInfoForTasks),
  mergeMap((action) => from(JobsService.getSessionInfoForTasks(action.payload)).pipe(
    map((data: any) => new LoadSessionInfoForTasksSuccess(data)),
    catchError((error: Error) => of(
      new LoadSessionInfoForTasksError(error.message || 'Unable to load sessions data.'),
      new CreateErrorNotice('Error', 'Failed to load sessions data.'),
    )),
  )),
);

const loadFeedbacksEpic = (action$: Observable<JobAction>) => action$.pipe(
  ofType<LoadFeedbacks>(JobsActions.LoadFeedbacks),
  mergeMap((action) => from(JobsService.loadFeedbacks(action.payload)).pipe(
    map((data: any) => new LoadFeedbacksSuccess(data)),
    catchError((error: Error) => of(
      new LoadFeedbacksError(error.message || 'Unable to load feedbacks data.'),
      new CreateErrorNotice('Error', 'Failed to load feedbacks data.'),
    )),
  )),
);

const JobEpics = [
  createJobEpic,
  updateJobEpic,
  getJobsEpic,
  getJobEpic,
  startJobEpic,
  completeJobEpic,
  updateJobPhaseEpic,
  removeJobEpic,
  reassignJobEpic,
  jobsPollEpic,
  cancelJobEpic,
  searchJobsEpic,
  searchJobsPollEpic,
  startJobsPollingAfterJobCreationEpic,
  loadSessionForTasksEpic,
  loadFeedbacksEpic,
];

export default JobEpics;
